import React, { memo } from "react";
import styled from "styled-components";

const MarkdownWrapper = styled.div`
	word-wrap: break-word;
	white-space: pre-wrap;
	
	p {
		margin: 0 0 8px 0;
		&:last-child {
			margin-bottom: 0;
		}
	}
	
	strong, b {
		font-weight: 600;
	}
	
	em, i {
		font-style: italic;
	}
	
	a {
		color: rgb(80, 76, 154);
		text-decoration: underline;
		&:hover {
			color: rgb(71, 67, 136);
		}
	}
	
	ul, ol {
		margin: 8px 0;
		padding-left: 20px;
	}
	
	li {
		margin: 4px 0;
	}
	
	code {
		background: rgba(0, 0, 0, 0.05);
		padding: 2px 6px;
		border-radius: 3px;
		font-family: 'Courier New', monospace;
		font-size: 0.9em;
	}
	
	pre {
		background: rgba(0, 0, 0, 0.05);
		padding: 12px;
		border-radius: 6px;
		overflow-x: auto;
		margin: 8px 0;
		
		code {
			background: none;
			padding: 0;
		}
	}
	
	blockquote {
		border-left: 3px solid rgb(80, 76, 154);
		padding-left: 12px;
		margin: 8px 0;
		color: #666;
	}
`;

const parseSimpleMarkdown = text => {
	if (!text || typeof text !== "string") return text;

	let html = text
		.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
		.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
		.replace(/\*(.+?)\*/g, "<em>$1</em>")
		.replace(/__(.+?)__/g, "<strong>$1</strong>")
		.replace(/_(.+?)_/g, "<em>$1</em>")
		.replace(/`(.+?)`/g, "<code>$1</code>")
		.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

	const lines = html.split("\n");
	const processed = [];
	let inList = false;
	let inOrderedList = false;
	let inCodeBlock = false;
	let codeBlockContent = [];

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		if (line.trim().startsWith("```")) {
			if (inCodeBlock) {
				processed.push(`<pre><code>${codeBlockContent.join("\n")}</code></pre>`);
				codeBlockContent = [];
				inCodeBlock = false;
			} else {
				inCodeBlock = true;
			}
			continue;
		}

		if (inCodeBlock) {
			codeBlockContent.push(line);
			continue;
		}

		const trimmed = line.trim();

		const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
		if (headingMatch) {
			if (inList) {
				processed.push("</ul>");
				inList = false;
			}
			if (inOrderedList) {
				processed.push("</ol>");
				inOrderedList = false;
			}

			const level = headingMatch[1].length;
			const headingText = headingMatch[2];
			processed.push(`<h${level}>${headingText}</h${level}>`);
			continue;
		}

		if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
			if (!inList) {
				processed.push("<ul>");
				inList = true;
			}
			processed.push(`<li>${trimmed.substring(2)}</li>`);
		} else if (trimmed.match(/^\d+\.\s/)) {
			if (!inOrderedList) {
				processed.push("<ol>");
				inOrderedList = true;
			}
			processed.push(`<li>${trimmed.replace(/^\d+\.\s/, "")}</li>`);
		} else if (trimmed.startsWith("> ")) {
			processed.push(`<blockquote>${trimmed.substring(2)}</blockquote>`);
		} else {
			if (inList) {
				processed.push("</ul>");
				inList = false;
			}
			if (inOrderedList) {
				processed.push("</ol>");
				inOrderedList = false;
			}
			if (trimmed) {
				processed.push(`<p>${line}</p>`);
			}
		}
	}

	if (inList) processed.push("</ul>");
	if (inOrderedList) processed.push("</ol>");

	return processed.join("");
};

const MarkdownContent = memo(({ content, onLinkClick }) => {
	const htmlContent = parseSimpleMarkdown(content);

	const handleClick = (e) => {
		if (e.target.tagName === 'A') {
			const href = e.target.getAttribute('href');
			if (href && onLinkClick) {
				onLinkClick(href);
			}
		}
	};

	return (
		<MarkdownWrapper
			dangerouslySetInnerHTML={{ __html: htmlContent }}
			onClick={handleClick}
		/>
	);
});

MarkdownContent.displayName = "MarkdownContent";

export default MarkdownContent;
